/**
 * "Which sub-route of the bound entity is the user currently on?" —
 * stack-based pub-sub published by sub-route components (DMA dashboard,
 * DMA single-matter page, Financial Effects, Net Impact / SDG / EU Taxonomy
 * / EU PAIs lens content) and read by `ChatContextCard` to decorate the
 * sentence beyond the bare entity name. Drives the Tier-B form of the card:
 *
 *   Discussing **Acme** · Double materiality on Climate change mitigation (Tailored)
 *   └── entity (binding) ──┘ └────────── path context (this store) ───────┘
 *
 * Why a separate store from `CurrentPageChatBinding`:
 *
 *   - The entity binding is published by the *outer* entity view
 *     (`CompanyView` / `CompanyGroupView` / `PortfolioView`), which stays
 *     mounted across sub-route navigations. Reusing the binding for the
 *     path descriptor would require either fragile merge semantics or a
 *     stack of publishers — and on inner-route unmount, the binding's
 *     "revert to null on unmount" lifecycle would clear the outer entity
 *     too. Keeping path context in its own store sidesteps both problems.
 *   - The descriptor is naturally read in two different places that don't
 *     both want the entity binding: the chat-context card (visual), and
 *     potentially the chat adapter later (hidden context line). The split
 *     keeps each consumer narrow.
 *
 * Why a stack rather than a single slot:
 *
 *   - `CompanyCsrdView` publishes `{ lensLabel: 'Double materiality' }`,
 *     and a `SingleMatterView` mounted inside it publishes
 *     `{ lensLabel: 'Double materiality', detailLabel: '<matter>' }`.
 *     When the matter page unmounts back to the dashboard, the dashboard's
 *     descriptor needs to "win" again — not get cleared. A stack gives us
 *     "topmost active publisher wins" for free.
 *   - Updates to a publisher's *value* (e.g. tailoring-variant toggles)
 *     mutate that publisher's slot in place, so position in the stack is
 *     preserved across re-renders. Removal happens only on the publisher
 *     unmounting (route change), not on every value change.
 *
 * Lifecycle (per the `usePublishCurrentPageChatPathContext` hook):
 *
 *   - Mount with non-empty descriptor → push entry, become top.
 *   - Re-render with new descriptor content → update entry in place.
 *   - Re-render with a now-empty descriptor (all fields undefined) →
 *     remove entry; the next-most-recent publisher takes the top.
 *   - Unmount → remove entry unconditionally.
 *
 * Sub-route publishers (all use `usePublishCurrentPageChatPathContext`):
 *
 *   - `MainCompanyView` `LensAndContent` — lens label for net impact /
 *     SDGs / EU taxonomy / EU PAIs / insights / targets.
 *   - `CompanyCsrdView` — `{ lensLabel: 'Double materiality', variantLabel? }`.
 *   - `SingleMatterView` — `{ lensLabel: 'Double materiality', detailLabel: <matter title>, variantLabel? }`.
 *   - `CompanyFeView` — `{ lensLabel: 'Financial effects', variantLabel? }`.
 *   - CompanyGroup / Portfolio counterparts for the lens-level views.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { ImpactLensType } from '@upright/common/impact-lenses/types';

export interface CurrentPageChatPathContext {
  /**
   * Human-readable lens / sub-route label. Examples: `'Net impact'`,
   * `'Double materiality'`, `'Financial effects'`, `'UN SDG alignment'`,
   * `'EU taxonomy'`, `'EU SFDR PAIs'`.
   *
   * Rendered after the entity name as `… · {lensLabel}`. The card
   * lower-cases nothing — pass the casing you want to appear.
   */
  lensLabel?: string;
  /**
   * Optional further qualifier — e.g. the matter title on a DMA
   * single-matter page (`'Climate change mitigation'`), or potentially an
   * impact-category name on a Net-Impact drilldown later. Rendered as
   * `… on {detailLabel}` after `{lensLabel}`.
   *
   * Only meaningful when `lensLabel` is also set; the card defends
   * against detail-without-lens by skipping the `· … on …` segment if
   * `lensLabel` is missing.
   */
  detailLabel?: string;
  /**
   * Optional variant indicator. Examples: `'Tailored'` (DMA tailoring
   * variant selected), `'Pre-release'` (non-default release). Rendered
   * in parentheses at the end of the decorator: `… ({variantLabel})`.
   */
  variantLabel?: string;
}

/**
 * Friendly lens label for the chat-context decorator. Diverges from
 * `friendlyImpactLens` in `impact-lens-selector.tsx` in two places:
 *
 *   - `UPRIGHT_NET_IMPACT → 'Net impact'` (vs `'Upright net impact'`):
 *     the card already prefixes with "Discussing", so the "Upright"
 *     branding is redundant. "Net impact" is also what the wireframe
 *     spec called it.
 *   - `CSRD → 'Double materiality'` (vs `'Materiality'`): the wireframe
 *     example reads `· Double materiality on Climate change mitigation`,
 *     and the DMA / single-matter publishers already hard-code `'Double
 *     materiality'`; mapping the enum here keeps them consistent.
 *
 * Pure switch over the enum — exhaustive, so adding a new lens forces
 * the callsite to decide on a label rather than silently rendering
 * `undefined`.
 */
export function lensLabelForImpactLens(lens: ImpactLensType): string {
  switch (lens) {
    case ImpactLensType.UPRIGHT_NET_IMPACT:
      return 'Net impact';
    case ImpactLensType.SDGS:
      return 'UN SDG alignment';
    case ImpactLensType.EU_TAXONOMY:
      return 'EU taxonomy';
    case ImpactLensType.EU_PAIS:
      return 'EU SFDR PAIs';
    case ImpactLensType.INSIGHTS:
      return 'Company insights';
    case ImpactLensType.TARGETS:
      return 'Company targets';
    case ImpactLensType.CSRD:
      return 'Double materiality';
    case ImpactLensType.FINANCIAL_EFFECTS:
      return 'Financial effects';
    case ImpactLensType.CLIMATE_RISK:
      return 'Climate risk';
  }
}

/**
 * PLT-6779: the lens label as the chat prompt names it, for the `lens=` field of
 * the hidden `[Context: …]` line (`upright-chat-adapter.ts`). Keyed by the labels
 * this module's vocabulary produces — `lensLabelForImpactLens` above plus the
 * literals the DMA / single-matter / financial-effects publishers pass.
 *
 * Deliberately NOT the same vocabulary: the prompt's "## Lens-specific routing"
 * section is generated from `LENSES` in
 * `src/services/main/upserve/modules/mcp/lenses.ts`, whose display names are the
 * ones the model is told to route by, and three of them read differently here
 * ('UN SDG alignment' vs 'SDG alignment', 'EU taxonomy' vs 'EU Taxonomy',
 * 'EU SFDR PAIs' vs 'SFDR'). The UI cannot import that backend module, so this is
 * the translation. Send the model the name its own routing table uses, not the
 * one the chip happens to show.
 *
 * Views that are not one of those lenses are deliberately absent — Company
 * insights, Company targets, Climate risk. Being on one of them says nothing
 * about which framework a question is about, and site / climate questions have
 * their own tools outside the lens routing. An unmapped label omits the field,
 * which leaves the caller's default framework in force: the fail-safe direction.
 */
const CHAT_LENS_SIGNAL_BY_LABEL: Readonly<Record<string, string>> = {
  'Net impact': 'Net impact',
  'Double materiality': 'Double materiality',
  'Financial effects': 'Financial effects',
  'UN SDG alignment': 'SDG alignment',
  'EU taxonomy': 'EU Taxonomy',
  'EU SFDR PAIs': 'SFDR',
};

/**
 * The `lens=` value to send for a path context, or `null` when the view the user
 * is on carries no framework signal (see `CHAT_LENS_SIGNAL_BY_LABEL`).
 */
export function chatLensSignalForPathContext(context: CurrentPageChatPathContext | null): string | null {
  if (!context?.lensLabel) return null;
  return CHAT_LENS_SIGNAL_BY_LABEL[context.lensLabel] ?? null;
}

// Internal stack entry. Mutated in place so re-renders that change the
// value don't shift the entry's position in the stack — only mount /
// unmount move entries in or out. The wrapper-object identity is what
// the hook tracks via a ref.
interface Entry {
  value: CurrentPageChatPathContext;
}

const _stack: Entry[] = [];
const _listeners = new Set<() => void>();

// "Sticky" mirror of the last non-null top-of-stack value. The stack is the
// authoritative source while a publisher is mounted, but when the user
// expands the chat side-panel to full `/chat`, every sub-route publisher
// (and the entity view above them) unmounts and the stack drains to empty.
// The chat-context card would otherwise lose its decorator mid-
// conversation. The sticky slot keeps the last observed descriptor for the
// lifetime of the conversation, cleared explicitly on `New chat` via
// `resetStickyPageChatPathContext`.
let _stickyValue: CurrentPageChatPathContext | null = null;
const _stickyListeners = new Set<() => void>();

function notify(): void {
  // Update the sticky mirror BEFORE notifying live subscribers so a
  // consumer that reads both stores in the same effect sees a consistent
  // pair (sticky === live whenever live is non-null).
  const top = topValue();
  if (top !== null && _stickyValue !== top) {
    _stickyValue = top;
    for (const l of _stickyListeners) l();
  }
  for (const l of _listeners) l();
}

function topValue(): CurrentPageChatPathContext | null {
  return _stack.length > 0 ? _stack[_stack.length - 1].value : null;
}

/** Direct read, for non-React callers (chat adapter etc.). */
export function getCurrentPageChatPathContext(): CurrentPageChatPathContext | null {
  return topValue();
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function subscribeCurrentPageChatPathContext(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

/**
 * Reactive accessor for React components — re-renders when the
 * top-of-stack descriptor changes.
 */
export function useCurrentPageChatPathContext(): CurrentPageChatPathContext | null {
  return useSyncExternalStore(subscribeCurrentPageChatPathContext, getCurrentPageChatPathContext);
}

/** Direct read of the sticky slot, for non-React callers. */
export function getStickyPageChatPathContext(): CurrentPageChatPathContext | null {
  return _stickyValue;
}

export function subscribeStickyPageChatPathContext(listener: () => void): () => void {
  _stickyListeners.add(listener);
  return () => {
    _stickyListeners.delete(listener);
  };
}

/**
 * Reactive accessor that returns the most-recent non-null path descriptor
 * for the lifetime of the current conversation (cleared on `New chat`).
 * Use this from the chat-context card so the decorator survives navigating
 * from a panel-bound entity sub-route to full `/chat` (where every
 * sub-route publisher unmounts).
 */
export function useStickyPageChatPathContext(): CurrentPageChatPathContext | null {
  return useSyncExternalStore(subscribeStickyPageChatPathContext, getStickyPageChatPathContext);
}

/**
 * Clear the sticky slot. Wired into `resetChatLoggingState` so `New chat`
 * also drops the lens / matter / variant decorator back to nothing.
 */
export function resetStickyPageChatPathContext(): void {
  if (_stickyValue === null) return;
  _stickyValue = null;
  for (const l of _stickyListeners) l();
}

/**
 * `true` iff the descriptor has at least one field set. An all-undefined
 * descriptor (`{}`, or `{ lensLabel: cond ? x : undefined, ... }` where
 * every cond is falsy) is treated as "no descriptor" rather than as an
 * orphan empty entry — the card would otherwise render a bare ` · `
 * separator with nothing after it.
 */
function isNonEmpty(value: CurrentPageChatPathContext | null): value is CurrentPageChatPathContext {
  if (value === null) return false;
  return value.lensLabel !== undefined || value.detailLabel !== undefined || value.variantLabel !== undefined;
}

/**
 * Helper hook for sub-route components. Publishes the path context with
 * stack semantics:
 *
 *   - First mount with a non-empty descriptor → push entry.
 *   - Re-render with new descriptor content → update entry in place
 *     (position in the stack unchanged).
 *   - Re-render with an empty / null descriptor → remove entry; the
 *     next-most-recent publisher (if any) takes the top.
 *   - Unmount → remove entry.
 *
 * Pass `null` (or an object with all fields undefined) at any time to
 * temporarily suppress this publisher's contribution without unmounting.
 *
 * Position in the stack reflects *mount order*, not last-update order —
 * so when a child route unmounts, the parent's entry naturally becomes
 * top again without the parent having to re-publish.
 */
export function usePublishCurrentPageChatPathContext(value: CurrentPageChatPathContext | null): void {
  // The stack entry owned by *this* hook invocation. Persisted across
  // renders so updates mutate in place. `null` while we have no entry in
  // the stack (either because the descriptor was empty on mount or we've
  // popped ourselves due to an empty update).
  const entryRef = useRef<Entry | null>(null);

  // Stringify the descriptor for the effect dep so re-renders with a
  // fresh object of the same content don't churn. Fields are a small
  // flat set of optional strings — JSON.stringify is cheap here.
  const valueKey = value === null ? '' : JSON.stringify(value);

  // Mount / update effect — push, update in place, or pop based on
  // current descriptor + previous entry state.
  useEffect(() => {
    if (isNonEmpty(value)) {
      if (entryRef.current === null) {
        const entry: Entry = { value };
        _stack.push(entry);
        entryRef.current = entry;
      } else {
        // Replace `.value` so the top-of-stack read picks up the new
        // content. Position in `_stack` is preserved — we explicitly do
        // not pop-and-re-push, because that would shift this entry to
        // the top of any sibling publishers and break the mount-order
        // semantics.
        entryRef.current.value = value;
      }
      notify();
    } else if (entryRef.current !== null) {
      // Empty descriptor on a previously-active publisher → pop. Next
      // re-render with a non-empty value will push again at the (new)
      // top, which is fine: the typical caller pattern is "always have
      // a value while mounted", and an entry that toggles in/out is
      // expected to lose its previous mount-order position.
      const idx = _stack.indexOf(entryRef.current);
      if (idx !== -1) _stack.splice(idx, 1);
      entryRef.current = null;
      notify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  // Unmount-only cleanup. Empty deps so cleanup fires on unmount, not
  // on every value change — the update effect above already handles
  // value transitions while mounted.
  useEffect(() => {
    return () => {
      if (entryRef.current !== null) {
        const idx = _stack.indexOf(entryRef.current);
        if (idx !== -1) _stack.splice(idx, 1);
        entryRef.current = null;
        notify();
      }
    };
  }, []);
}
